import React from 'react';
import { Award, Star, CheckCircle } from 'lucide-react';

const HeroDesignComparison: React.FC = () => {
  const company = "Silver International SA";
  const score = 75;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Comparaison des 3 Options de Hero Section</h1>
            <p className="text-gray-600 mt-2">Choisissez le style qui convient le mieux à votre entreprise</p>
          </div>
        </div>
      </header>

      <div className="py-12 space-y-16">
        
        {/* Option 1: Minimalisme Corporate */}
        <div className="bg-white">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Option 1: Minimalisme Corporate</h2>
              <p className="text-gray-600">Style épuré et professionnel, idéal pour les entreprises traditionnelles</p>
            </div>
            
            {/* Hero Section - Style Corporate Minimaliste */}
            <section className="relative bg-white py-20 lg:py-32 border-2 border-dashed border-gray-300 rounded-lg">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="inline-flex items-center gap-3 mb-8 px-4 py-2 bg-gray-50 rounded-full border">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-600">Analyse digitale</span>
                  </div>
                  
                  <h1 className="text-4xl lg:text-6xl font-light text-gray-900 mb-6 leading-tight">
                    Découvrez ce qui freine le trafic de 
                    <span className="font-semibold text-blue-600"> {company}</span>
                  </h1>
                  
                  <div className="w-24 h-0.5 bg-blue-500 mx-auto mb-8"></div>
                  
                  <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
                    Analyse complète et recommandations personnalisées pour optimiser votre présence digitale
                  </p>
                  
                  <button className="bg-gray-900 text-white px-8 py-4 rounded-lg hover:bg-gray-800 transition-colors font-medium">
                    Commencer l'analyse
                  </button>
                  
                  <div className="mt-8 inline-flex items-center gap-2 text-gray-500">
                    <Star className="w-5 h-5" />
                    <span>Score Global {score}%</span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Option 2: Glassmorphism Subtil */}
        <div className="bg-white">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Option 2: Glassmorphism Subtil</h2>
              <p className="text-gray-600">Design moderne avec effets de transparence et profondeur</p>
            </div>
            
            {/* Hero Section - Glassmorphism Professionnel */}
            <section className="relative bg-gradient-to-br from-gray-50 to-blue-50/30 py-20 lg:py-32 border-2 border-dashed border-gray-300 rounded-lg">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23e5e7eb%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
              
              <div className="container mx-auto px-4 relative">
                <div className="max-w-5xl mx-auto">
                  <div className="backdrop-blur-sm bg-white/60 rounded-3xl border border-white/20 shadow-xl p-12 lg:p-16">
                    <div className="inline-flex items-center gap-2 mb-6 px-3 py-1 bg-blue-50/80 rounded-full text-sm font-medium text-blue-700">
                      <CheckCircle className="w-4 h-4" />
                      Audit digital gratuit
                    </div>
                    
                    <h1 className="text-4xl lg:text-5xl font-medium text-gray-900 mb-8 leading-tight">
                      Découvrez ce qui freine le trafic de 
                      <span className="text-blue-600"> {company}</span>
                    </h1>
                    
                    <p className="text-xl text-gray-600 mb-10 max-w-3xl">
                      Analyse complète et recommandations personnalisées pour optimiser votre présence digitale
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <button className="bg-blue-600 text-white px-8 py-4 rounded-xl hover:bg-blue-700 transition-all hover:shadow-lg font-medium">
                        Démarrer l'audit gratuit
                      </button>
                      <button className="border border-gray-300 text-gray-700 px-8 py-4 rounded-xl hover:bg-gray-50 transition-colors font-medium">
                        En savoir plus
                      </button>
                    </div>
                    
                    <div className="mt-8 flex items-center justify-center gap-2 text-gray-500">
                      <Star className="w-5 h-5 text-yellow-400 fill-current" />
                      <span>Score Global {score}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Option 3: Moderne Tech */}
        <div className="bg-white">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Option 3: Moderne Tech (Recommandée)</h2>
              <p className="text-gray-600">Design tech moderne avec dashboard preview et éléments interactifs</p>
            </div>
            
            {/* Hero Section - Style Tech Moderne */}
            <section className="relative bg-white py-20 lg:py-32 overflow-hidden border-2 border-dashed border-gray-300 rounded-lg">
              <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-blue-50/50 to-transparent"></div>
              <div className="absolute bottom-0 left-0 w-1/4 h-1/2 bg-gradient-to-t from-gray-50/50 to-transparent"></div>
              
              <div className="container mx-auto px-4 relative">
                <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 mb-6 px-3 py-1 bg-blue-50 rounded-full text-sm font-medium text-blue-700">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      Audit digital gratuit
                    </div>
                    
                    <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                      Découvrez ce qui freine le trafic de 
                      <span className="text-blue-600">{company}</span>
                    </h1>
                    
                    <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                      Analyse approfondie de votre présence digitale avec des recommandations concrètes pour améliorer vos performances.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                      <button className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl">
                        Commencer l'analyse
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-3 text-gray-600">
                      <div className="flex -space-x-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full border-2 border-white flex items-center justify-center">
                          <span className="text-xs font-semibold text-blue-600">A</span>
                        </div>
                        <div className="w-8 h-8 bg-green-100 rounded-full border-2 border-white flex items-center justify-center">
                          <span className="text-xs font-semibold text-green-600">B</span>
                        </div>
                        <div className="w-8 h-8 bg-purple-100 rounded-full border-2 border-white flex items-center justify-center">
                          <span className="text-xs font-semibold text-purple-600">C</span>
                        </div>
                      </div>
                      <span className="text-sm">+200 entreprises analysées</span>
                    </div>
                  </div>
                  
                  <div className="lg:pl-12">
                    <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 shadow-lg">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="font-medium text-gray-900">{company}</span>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Performance SEO</span>
                          <span className="font-semibold text-gray-900">{score}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div className="bg-blue-600 h-3 rounded-full transition-all duration-1000" style={{width: `${score}%`}}></div>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Vitesse de chargement</span>
                          <span className="font-semibold text-gray-900">82%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div className="bg-green-500 h-3 rounded-full transition-all duration-1000" style={{width: '82%'}}></div>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Expérience utilisateur</span>
                          <span className="font-semibold text-gray-900">68%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div className="bg-yellow-500 h-3 rounded-full transition-all duration-1000" style={{width: '68%'}}></div>
                        </div>
                      </div>
                      
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Award className="w-4 h-4" />
                          <span>Dernière analyse: Aujourd'hui</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Navigation pour choisir */}
        <div className="bg-gray-900 text-white py-12">
          <div className="container mx-auto px-4 text-center">
            <h3 className="text-2xl font-bold mb-4">Quelle option préférez-vous ?</h3>
            <p className="text-gray-300 mb-8">Chaque style a ses avantages selon votre secteur d'activité</p>
            
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-gray-800 rounded-lg p-6">
                <h4 className="font-semibold mb-2">Option 1: Corporate</h4>
                <p className="text-sm text-gray-300">Parfait pour: Cabinets, consultants, services B2B traditionnels</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-6">
                <h4 className="font-semibold mb-2">Option 2: Glassmorphism</h4>
                <p className="text-sm text-gray-300">Parfait pour: Startups, agences créatives, services modernes</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-6 border-2 border-blue-500">
                <h4 className="font-semibold mb-2">Option 3: Tech ⭐</h4>
                <p className="text-sm text-gray-300">Parfait pour: SaaS, tech, services digitaux, données</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroDesignComparison;
